import { Producers } from '../../../constants/producers';
import { TicketType as TicketTypeValues } from '../../../constants/tickets';
import { Event } from '../../../types/event';
import { Membership } from '../../../types/membership';
import { Ticket } from '../../../types/ticket';
import { ValidationStrategy } from './validationStrategy';
const { COMMON, CHECKIN_MEMBER, FAN_MEMBER } = TicketTypeValues;

interface CommonTicket {
  commonQty: number;
  limitReached: number;
}

export class BahiaStrategy extends ValidationStrategy {
  /**
   * Checks if the given event is a Bahia event based on its producer ID.
   *
   * @param {Event | null} event - The event to check.
   * @returns {boolean} Returns `true` if the event is a Bahia event, otherwise `false`.
   */
  protected _isBahiaEvent(event: Event | null): boolean {
    const bahiaProducerIds = Producers.Bahia;
    return bahiaProducerIds.includes(event?.producerId || 0);
  }

  protected validateCommonTickets(tickets: Ticket[], cart: Ticket[], ticketId: number, quantityTotal: number): boolean {
    const commonTicketsId = tickets
      .flatMap(item => item.type)
      .filter(t => Number(t.category.id) === COMMON)
      .map(t => t.id);

    if ( cart.length > 0 && commonTicketsId.includes(ticketId) ) {
      const commonTicket = cart.reduce((sum, item) => {
        const validTypes = item.type.filter(type => Number(type.category.id) === COMMON);
        return sum + validTypes.reduce((typeSum, type) => typeSum + type.quantityInCart, 0);
      }, 0);
      if (commonTicket >= quantityTotal) {
        const result = tickets.some(ticket => ticket.type.some((type) => {
          if (Number(type.category.id) === COMMON) {
            return true;
          }
        }));
        return result;
      }
    }
    return false;
  }
}

export class MembershipBahiaStrategy extends BahiaStrategy {
  shouldApply(membershipApplied: Membership | null, event: Event | null) {
    return !!membershipApplied && this._isBahiaEvent(event);
  }

  validate(
    membershipApplied: Membership | null,
    cart: Ticket[],
    event: Event | null,
    tickets: Ticket[],
    groupId: number,
    ticketId: number,
    totalTickets: number
  ) {
    if (this.validateCommonTickets(tickets, cart, ticketId, 2)) {
      return { disableAddToCart : true, errorMessage : null };
    }

    const baseValidation = super.validate(membershipApplied, cart, event, tickets, groupId, ticketId, totalTickets);
    if (baseValidation.disableAddToCart) return baseValidation;

    const containsMoreThanMax = totalTickets >= 2;
    return { disableAddToCart : containsMoreThanMax, errorMessage : null };
  }
}

export class EsquadraoNaFonteStrategy extends BahiaStrategy {
  shouldApply(membershipApplied: Membership, event: Event) {
    return membershipApplied?.name.startsWith('ESQUADRAONAFONTE') && this._isBahiaEvent(event);
  }

  validate(
    membershipApplied: Membership | null,
    cart: Ticket[],
    event: Event | null,
    tickets: Ticket[],
    groupId: number,
    ticketId: number,
    totalTickets: number
  ) {
    const commonTicketQty = this.getQtyOfCommonTickets(tickets, cart);
    if (this.validateCommonTickets(tickets, cart, ticketId, commonTicketQty.commonQty))
      return { disableAddToCart : true, errorMessage : null };

    const baseValidation = super.validate(membershipApplied, cart, event, tickets, groupId, ticketId, totalTickets);
    if (baseValidation.disableAddToCart) return baseValidation;

    const containsMoreThanMax = totalTickets >= commonTicketQty.limitReached;
    return { disableAddToCart : containsMoreThanMax, errorMessage : null };
  }

  private getQtyOfCommonTickets(tickets: Ticket[], cart: Ticket[]): CommonTicket {
    const checkinTickets = tickets.some(
      ticket => ticket.type.some(type => Number(type.category.id) === CHECKIN_MEMBER && type.status === 'available')
    );
    if (checkinTickets) return { commonQty : 2, limitReached : 3 };

    const fanMemberTickets = cart.some(
      ticket => ticket.type.some(type => Number(type.category.id) === FAN_MEMBER && type.status === 'available')
    );
    return fanMemberTickets ? { commonQty : 1, limitReached : 2 }: { commonQty : 2, limitReached : 2 };
  }
}
